// src/pages/components/VendasMensaisVendedoresDashboard.js
import { useEffect, useMemo, useState } from "react";

import { useTheme } from "@mui/material/styles";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import { Card, CircularProgress, Tooltip } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function monthLabel(ano, mes) {
  const labels = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];
  const mm = Number(mes) || 1;
  const aa = String(ano || "").slice(-2);
  return `${labels[mm - 1] || "?"} ${aa}`;
}

export default function VendasMensaisVendedoresDashboard({
  height = "16rem",
  fullscreen = false,
  autoLoad = true,
  refreshToken = 0,
  onLoadingChange,
  onLoadedChange,
  onEmptyChange,
}) {
  const theme = useTheme();
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const isAdminLike = useMemo(
    () => temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria"),
    [temFuncao]
  );

  const isComercial = useMemo(() => temFuncao("Comercial"), [temFuncao]);

  const refUser = useMemo(() => userData?.refvendedor?.toString?.() || "", [userData]);

  const idUtilizador = useMemo(() => Number(userData?.id || 0), [userData]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]); // [{ ano, mes, refvendedor, vendedor, total }]
  const [vendedorSel, setVendedorSel] = useState("");

  const colorMap = {
    primary: theme.palette.primary.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    secondary: theme.palette.secondary.main,
    dark: theme.palette.dark.main,
  };

  const carregar = async () => {
    setLoading(true);
    onLoadingChange?.(true);

    try {
      const body = {
        idutilizador: idUtilizador,
      };

      const [data, err] = await handleApi(
        api.post("Dashboard/SelVendasMensaisVendedores13Meses", body)
      );

      if (err) {
        showAlert(err, "error");
        setRows([]);
        return;
      }

      const lista = Array.isArray(data) ? data : [];

      setRows(lista);

      const vendedoresDisponiveis = new Set(
        lista.map((r) => String(r?.refvendedor ?? "").trim()).filter(Boolean)
      );

      setVendedorSel((anterior) => {
        // Mantém a seleção atual se continuar disponível.
        if (anterior && vendedoresDisponiveis.has(anterior)) {
          return anterior;
        }

        // Dá sempre preferência ao vendedor próprio.
        if (refUser && vendedoresDisponiveis.has(refUser)) {
          return refUser;
        }

        // Fallback, sobretudo para admin-like sem refvendedor associada.
        return [...vendedoresDisponiveis][0] || "";
      });

      onLoadedChange?.(true);

      onEmptyChange?.(lista.length === 0);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  useEffect(() => {
    if (!idUtilizador) {
      onLoadingChange?.(false);
      return;
    }

    if (!isAdminLike && isComercial && !refUser) {
      onLoadingChange?.(false);
      return;
    }

    if (!autoLoad && refreshToken <= 0) return;

    carregar();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idUtilizador, isAdminLike, isComercial, refUser, autoLoad, refreshToken]);

  const chart = useMemo(() => {
    if (!vendedorSel) {
      return {
        labels: [],
        datasets: [],
        vendedoresOptions: [],
      };
    }

    const now = new Date();

    // Período atual: mês de há 12 meses até ao mês atual.
    const periodoAtual = [];
    for (let i = 12; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);

      periodoAtual.push({
        ano: d.getFullYear(),
        mes: d.getMonth() + 1,
      });
    }

    // Mesmo período, exatamente um ano antes.
    const periodoAnterior = periodoAtual.map((m) => ({
      ano: m.ano - 1,
      mes: m.mes,
    }));

    const mesesNome = [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ];

    // Os dois períodos ocupam exatamente as mesmas posições no eixo X.
    const labels = periodoAtual.map((m) => mesesNome[m.mes - 1] || "?");

    const rowsVendedor = (Array.isArray(rows) ? rows : []).filter(
      (r) => String(r?.refvendedor ?? "").trim() === String(vendedorSel).trim()
    );

    const valorPorMes = new Map();

    rowsVendedor.forEach((r) => {
      const ano = Number(r?.ano);
      const mes = Number(r?.mes);

      valorPorMes.set(`${ano}-${pad2(mes)}`, Number(r?.total) || 0);
    });

    const dadosAtual = periodoAtual.map((m) => valorPorMes.get(`${m.ano}-${pad2(m.mes)}`) || 0);

    const dadosAnterior = periodoAnterior.map(
      (m) => valorPorMes.get(`${m.ano}-${pad2(m.mes)}`) || 0
    );

    const primeiroAtual = periodoAtual[0];
    const ultimoAtual = periodoAtual[periodoAtual.length - 1];

    const primeiroAnterior = periodoAnterior[0];
    const ultimoAnterior = periodoAnterior[periodoAnterior.length - 1];

    const labelPeriodo = (ini, fim) =>
      `${monthLabel(ini.ano, ini.mes)} – ${monthLabel(fim.ano, fim.mes)}`;

    const vendedoresMap = new Map();

    (Array.isArray(rows) ? rows : []).forEach((r) => {
      const ref = String(r?.refvendedor ?? "").trim();
      if (!ref) return;

      const nome = String(r?.vendedor ?? "").trim() || ref;

      if (!vendedoresMap.has(ref)) {
        vendedoresMap.set(ref, {
          ref,
          nome,
        });
      }
    });

    const vendedoresOptions = [...vendedoresMap.values()].sort((a, b) =>
      String(a.nome).localeCompare(String(b.nome), "pt-PT")
    );

    const vendedorAtual = vendedoresMap.get(String(vendedorSel).trim()) || null;

    return {
      labels,

      datasets: [
        {
          label: labelPeriodo(primeiroAtual, ultimoAtual),
          data: dadosAtual,
          color: "warning",
          tension: 0.35,
        },
        {
          label: labelPeriodo(primeiroAnterior, ultimoAnterior),
          data: dadosAnterior,
          color: "primary",
          tension: 0.35,
        },
      ],

      vendedoresOptions,
      vendedorAtual,

      totalAtual: dadosAtual.reduce((acc, x) => acc + (Number(x) || 0), 0),

      totalAnterior: dadosAnterior.reduce((acc, x) => acc + (Number(x) || 0), 0),
    };
  }, [rows, vendedorSel]);

  const fmt = useMemo(
    () =>
      new Intl.NumberFormat("pt-PT", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    []
  );

  // console.log(height);

  return (
    <MDBox mb={2}>
      <Card
        sx={
          fullscreen
            ? undefined
            : {
                boxShadow: "none",
                border: "none",
                backgroundColor: "transparent",
                overflow: "visible",
              }
        }
      >
        <MDBox p={fullscreen ? 1.5 : 0}>
          {fullscreen && (
            <MDTypography variant="h6" gutterBottom>
              Vendas mensais por vendedor (últimos 13 meses)
            </MDTypography>
          )}

          {/* <MDTypography
            variant="caption"
            color="text"
            sx={{
              display: "block",
              mb: 1.25,
              mt: fullscreen ? 0 : -1,
              whiteSpace: "normal",
            }}
          >
            Top 5 vendedores + linha “Outros”. Na opção “Outros (agregado)”, a soma das linhas por
            mês será igual ao total mensal do gráfico “Vendas dos últimos 3 anos”.
          </MDTypography> */}

          {loading ? (
            <MDBox py={4} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </MDBox>
          ) : !chart || (chart.datasets?.length || 0) === 0 ? (
            <MDTypography variant="button" color="text">
              Sem dados para apresentar.
            </MDTypography>
          ) : (
            <>
              <MDBox mb={1.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {chart?.vendedoresOptions?.length > 1 && (
                  <MDBox sx={{ width: { xs: "100%", sm: 320 } }}>
                    <select
                      value={vendedorSel}
                      onChange={(e) => setVendedorSel(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: 6,
                        fontSize: 13,
                        border: "1px solid #ccc",
                      }}
                    >
                      {chart.vendedoresOptions.map((o) => (
                        <option key={o.ref} value={o.ref}>
                          {o.nome} ({o.ref})
                        </option>
                      ))}
                    </select>
                  </MDBox>
                )}

                {/* {!fullscreen && height === "16rem" ? (
                  <Tooltip title="Abrir em tamanho grande">
                    <MDButton
                      color="info"
                      size="small"
                      onClick={() =>
                        window.open(
                          "/dashboard/vendas-mensais-vendedores",
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                      sx={{
                        minWidth: "auto",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <OpenInNewIcon fontSize="small" sx={{ mr: 0.75 }} />
                      Abrir
                    </MDButton>
                  </Tooltip>
                ) : null} */}
              </MDBox>

              <MDBox mb={1.5}>
                <DefaultLineChart
                  chart={{
                    labels: chart.labels,
                    datasets: chart.datasets,
                  }}
                  height={height}
                />
              </MDBox>

              {/* legenda */}
              <MDBox display="flex" flexWrap="wrap" gap={1.5}>
                {chart.datasets.map((ds) => (
                  <MDTypography
                    key={ds.label}
                    variant="button"
                    color="text"
                    sx={{
                      fontSize: 12,
                      display: "inline-flex",
                      alignItems: "center",
                      mr: 1.5,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        marginRight: 6,
                        backgroundColor: colorMap[ds.color] || "#999",
                        display: "inline-block",
                      }}
                    />
                    {ds.label} (
                    {fmt.format(ds.color === "warning" ? chart.totalAtual : chart.totalAnterior)} €)
                  </MDTypography>
                ))}
              </MDBox>
            </>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
}
